// src/accounts/pages/EngagementDetailPage.jsx
//
// Project-scoped team roster and role grants for one Project. Route:
// /admin/projects/:engagementId. Rebuilt to match admin/pages/UsersPage.jsx's
// shared/ui-based style — PageHeader with breadcrumbs, a StatTile summary
// row, one DataTable, add/edit behind TeamMemberFormModal, ConfirmModal for
// removal. Moved from pages/EngagementDetailPage.jsx, replacing a
// hand-rolled <table>, an always-open inline form, window.confirm, and
// styles/EngagementsPage.css.
//
// Unlike AccountDetailPage, this route is NOT gated on a specific
// permission at the router level (a project-scoped viewer must still reach
// it) — so canManageTeam is checked here, and the whole Actions column is
// omitted, not just its buttons, for anyone without
// conductor:engagement:manage.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FolderGit2, Plus, Users, KeyRound, UserX } from "lucide-react";
import {
  PageHeader,
  Button,
  EmptyState,
  DataTable,
  StatusBadge,
  StatTile,
  Alert,
  ConfirmModal,
  SkeletonCardGrid,
  SkeletonTable,
} from "../../shared/ui";
import { conductorApi } from "../../api/conductorApi";
import { listRoles } from "../../admin/api/rolesApi";
import { toAdminError } from "../../admin/api/adminApiError";
import { useAuth } from "../../context/AuthContext";
import TeamMemberFormModal from "../components/TeamMemberFormModal";

export default function EngagementDetailPage() {
  const { engagementId } = useParams();
  const { hasPermission } = useAuth();

  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);

  // null = closed. { userId: null } = add, a member = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  // presentation/conductor/engagement_controller.py has no "get one
  // engagement" route returning the full team_members+granted_roles shape
  // (only list/create/mutate routes do — GET .../context returns a
  // different, read-only façade shape with no granted_roles at all) — find
  // this one from the list instead of adding a new backend route just for
  // this read.
  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Unfiltered by account on purpose — this page looks up one specific
      // engagementId regardless of whatever account happens to be selected
      // in the header switcher right now (e.g. a direct link into a
      // project under a different account).
      const { data: all } = await conductorApi.listEngagements();
      const found = (all || []).find((e) => e.engagement_id === engagementId);
      setEngagement(found || null);
      if (!found) setLoadError("Project not found, or not visible to you.");
    } catch (err) {
      setLoadError(toAdminError(err, "Failed to load the project.").message);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      // Only project-level roles are grantable here — see
      // GrantProjectRoleHandler; filtering server-side means the picker
      // never even offers a choice that would always fail.
      setRoles(await listRoles({ level: "project" }));
    } catch (err) {
      setRolesError(err?.message || "Failed to load assignable roles.");
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const canManageTeam = Boolean(
    engagement &&
      hasPermission("conductor:engagement:manage", {
        accountId: engagement.account_id,
        projectId: engagementId,
      })
  );

  const roleNameById = useCallback((id) => roles.find((r) => r.id === id)?.name, [roles]);

  // team_members already carries username/email from the backend join
  // (see TeamMemberRowDto) — no separate users fetch needed here.
  const members = useMemo(
    () =>
      (engagement?.team_members || []).map((m) => ({
        userId: m.user_id,
        username: m.username,
        email: m.email,
        role: m.role,
        roleIds: m.granted_roles || [],
      })),
    [engagement]
  );

  const handleSubmit = useCallback(
    async ({ userId, role, roleIds }) => {
      const roleNames = roleIds.map(roleNameById).filter(Boolean);
      try {
        // Adding someone is one call — addTeamMember grants role_names in
        // the same transaction it creates the roster row (see
        // AddTeamMemberHandler). Editing an existing member's roles still
        // needs its own call: there's no roster/label change involved,
        // just the role-set replace.
        const { data } = formTarget?.userId
          ? await conductorApi.setProjectRoles(engagementId, userId, roleNames)
          : await conductorApi.addTeamMember(engagementId, { user_id: userId, role, role_names: roleNames });
        setEngagement(data);
        const found = (data.team_members || []).find((m) => m.user_id === userId);
        return { username: found?.username || formTarget?.username || `User #${userId}` };
      } catch (err) {
        throw toAdminError(err, "Save failed. Please try again.");
      }
    },
    [engagementId, roleNameById, formTarget]
  );

  const handleRemove = useCallback(async () => {
    const { data } = await conductorApi.removeTeamMember(engagementId, removeTarget.userId);
    setEngagement(data);
    setRemoveTarget(null);
  }, [engagementId, removeTarget]);

  const withoutProjectRole = members.filter((m) => m.roleIds.length === 0).length;
  const totalRoleGrants = members.reduce((sum, m) => sum + m.roleIds.length, 0);

  const columns = useMemo(() => {
    const base = [
      {
        accessorKey: "username",
        header: "User",
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block font-medium text-ink">{row.original.username}</span>
            <span className="block text-xs text-muted mt-0.5">{row.original.email}</span>
          </div>
        ),
      },
      {
        accessorKey: "role",
        header: "Label",
        width: 160,
        cell: ({ getValue }) => <span className="text-muted">{getValue() || "—"}</span>,
      },
      {
        id: "roleCount",
        header: "Granted Roles",
        accessorFn: (row) => row.roleIds.length,
        cell: ({ row }) => {
          const ids = row.original.roleIds;
          if (ids.length === 0) return <span className="text-muted">—</span>;
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              {ids.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-[11px] font-semibold"
                >
                  {roleNameById(id) || `#${id}`}
                </span>
              ))}
            </div>
          );
        },
      },
    ];

    if (!canManageTeam) return base;

    return [
      ...base,
      {
        id: "actions",
        header: "",
        align: "right",
        width: 160,
        sortAllowed: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setFormTarget(row.original)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setRemoveTarget(row.original)}
            >
              Remove
            </Button>
          </div>
        ),
      },
    ];
  }, [canManageTeam, roleNameById]);

  if (loading && !engagement) {
    return (
      <div>
        <SkeletonCardGrid cards={3} className="mb-5" />
        <SkeletonTable rows={5} columns={3} className="rounded-card border border-line bg-white" />
      </div>
    );
  }

  if (!engagement) {
    return (
      <div>
        <PageHeader title="Project" breadcrumbs={[{ label: "Projects", href: "/admin/projects" }]} />
        <EmptyState
          icon={FolderGit2}
          title="Couldn't load project"
          description={loadError || "Project not found."}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={engagement.client_name}
        subtitle={
          <span className="inline-flex items-center gap-2">
            <StatusBadge map="engagementStage" status={engagement.current_stage} size="sm" />
            <span className="text-muted">Account #{engagement.account_id ?? "none (org-wide)"}</span>
          </span>
        }
        breadcrumbs={[{ label: "Projects", href: "/admin/projects" }, { label: engagement.client_name }]}
        actions={
          canManageTeam && (
            <Button variant="primary" onClick={() => setFormTarget({ userId: null })}>
              <Plus size={15} /> Add team member
            </Button>
          )
        }
      />

      {members.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatTile icon={Users} label="Team Members" value={members.length} />
          <StatTile icon={KeyRound} label="Total Role Grants" value={totalRoleGrants} />
          <StatTile
            icon={UserX}
            label="Without a Project Role"
            value={withoutProjectRole}
            hint={withoutProjectRole > 0 ? "On the team, but no project-scoped role" : undefined}
          />
        </div>
      )}

      {canManageTeam && rolesError && (
        <Alert variant="danger" title="Assignable roles unavailable" className="mb-4">
          {rolesError} Team members are listed below, but adding or editing one is disabled
          until this loads.{" "}
          <button type="button" onClick={loadRoles} className="underline font-semibold">
            Retry
          </button>
        </Alert>
      )}

      {members.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No team members yet"
          description={
            canManageTeam
              ? "Add the first person to this Project's team."
              : "Nobody has been added to this Project's team yet."
          }
          action={
            canManageTeam && (
              <Button variant="secondary" onClick={() => setFormTarget({ userId: null })}>
                <Plus size={15} /> Add team member
              </Button>
            )
          }
        />
      ) : (
        <DataTable table={{ columns, rows: members }} canSearch />
      )}

      {canManageTeam && (
        <>
          {/* Keyed so switching between add and editing a different row
              re-seeds the form's initial state instead of keeping stale input. */}
          <TeamMemberFormModal
            key={formTarget?.userId ?? "new"}
            open={Boolean(formTarget)}
            member={formTarget?.userId ? formTarget : null}
            roles={roles}
            rolesLoading={rolesLoading}
            rolesError={rolesError}
            onClose={() => setFormTarget(null)}
            onSubmit={handleSubmit}
          />

          <ConfirmModal
            open={Boolean(removeTarget)}
            entityLabel="team member"
            name={removeTarget?.username}
            confirmLabel="Remove"
            onClose={() => setRemoveTarget(null)}
            onConfirm={handleRemove}
          >
            {removeTarget?.username} is removed from this Project's team, along with every
            project-scoped role they held here.
          </ConfirmModal>
        </>
      )}
    </div>
  );
}
